import { DarwinListView } from 'components/darwin-ui';
import styles from './index.module.css';
import { useTranslation } from 'react-i18next';
import { getColumns } from './columns';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useEffect, useState, useMemo } from 'react';

import { useGetDataSourceFields } from 'api/services/dataSourceService';
import { filterDataSourceFields } from 'utils/dataSources';
import { DataSourceField } from 'types/dataSourcesTypes';
import { useParams } from 'react-router-dom';

interface DataSourceFieldsListProps {
  templateId: string;
  templateName: string;
  mappedValues: Record<string, string>;
  onMappedChange: (fieldId: string, value: string) => void;
}

export const DataSourceFieldsList = ({
  templateId,
  templateName,
  mappedValues,
  onMappedChange,
}: DataSourceFieldsListProps) => {
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const { id: dataSourceId } = useParams();

  const {
    data: fieldsData,
    isLoading,
    isError,
  } = useGetDataSourceFields({
    dataSourceId: dataSourceId || '',
    templateId: templateId,
  });

  // Filter fields based on the selected template key
  const rawFields = useMemo(() => fieldsData?.data ?? [], [fieldsData]);

  const filteredFields = useMemo(
    () => filterDataSourceFields(rawFields, searchQuery, t, mappedValues),
    [rawFields, searchQuery, t, mappedValues]
  );

  useEffect(() => {
    if (isError) {
      openNotification(
        t('error'),
        t('data-tables.fields-list.loading-error'),
        'error'
      );
    }
  }, [isError, t, openNotification]);

  const columns = getColumns(t, searchQuery, mappedValues, onMappedChange);

  return (
    <div className={styles.dataSourceFieldsContainer}>
      <DarwinListView<DataSourceField>
        data={filteredFields}
        isLoading={isLoading}
        columns={columns}
        header={{
          title: templateName,
          setSearchQuery,
        }}
        table={{
          scrollable: false,
        }}
      />
    </div>
  );
};
